import React, { useState, useEffect, useRef } from 'react';
import ServiceNavigation from '../ServiceNavigation.jsx';
import BrowserCheck from '../BrowserCheck';
import TopPanel from '../TopPanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import RequestFlowVisualization from './RequestFlowVisualization';
import StructuredResponse from './StructuredResponse';
import { io } from 'socket.io-client';
import { EXAMPLE_LOG } from './exampleLog.js';

import {
    AppLayout,
    Button,
    SpaceBetween,
    Container,
    Link,
    Box,
    Textarea,
    Icon,
    Header,
    Alert,
    ColumnLayout,
} from '@cloudscape-design/components';
import Avatar from "@cloudscape-design/chat-components/avatar";
import LoadingBar from "@cloudscape-design/chat-components/loading-bar";

import '../../styles/form.scss';
import './ridanalyzer.scss';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import { sendAnalytics, sendError } from '../../resources/rum-provider';
import { RID_ANALYZER_ENDPOINT } from '../../resources/prod-env.jsx';

const demoModule = "RIDAnalyzer";
const demoType = "tool";

export default function RIDAnalyzer(props) {
    const [messages, setMessages] = useState([]);
    const [currentInput, setCurrentInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState('');
    const [error, setError] = useState(null);
    const [sessionId] = useState(() => {
        // Generate a session ID that's at least 33 characters (AgentCore requirement)
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2);
        return `session-${timestamp}-${random}`;
    });
    const [connected, setConnected] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const socketRef = useRef(null);
    const [isUserScrolling, setIsUserScrolling] = useState(false);

    useEffect(() => {
        // Initialize socket.io connection
        const socket = io(RID_ANALYZER_ENDPOINT, {
            transports: ['websocket'],
            autoConnect: true,
            reconnection: true,
        });

        socket.on('connect', () => {
            console.log('Socket connected');
            setConnected(true);
            setError(null);
        });

        socket.on('disconnect', () => {
            console.log('Socket disconnected');
            setConnected(false);
        });

        socket.on('connect_error', (err) => {
            console.error('Connection error:', err);
            setError(`Connection error: ${err.message}`);
            setConnected(false);
        });

        // Listen for streaming response chunks
        socket.on('response_chunk', (data) => {
            // Clean up excessive whitespace in real-time
            const cleanedData = data.replace(/\n{3,}/g, '\n');
            setStreamingMessage(prev => prev + cleanedData);
        });

        // Listen for completion
        socket.on('response_complete', () => {
            setIsLoading(false);
            // Move streaming message to messages using callback to get current value
            setStreamingMessage(currentStreaming => {
                if (currentStreaming) {
                    setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: currentStreaming,
                        timestamp: new Date().toISOString()
                    }]);
                }
                return ''; // Clear streaming message
            });
        });

        // Listen for errors
        socket.on('error', (data) => {
            console.error('Socket error:', data);
            setError(data.message || 'An error occurred');
            setIsLoading(false);
            setStreamingMessage('');
        });

        socketRef.current = socket;

        // Send analytics on mount
        sendAnalytics({ demo: demoModule });

        // Cleanup on unmount
        return () => {
            socket.off('connect');
            socket.off('disconnect');
            socket.off('connect_error');
            socket.off('response_chunk');
            socket.off('response_complete');
            socket.off('error');
            socket.disconnect();
        };
    }, []);

    const scrollToBottom = () => {
        // Only auto-scroll if user is not manually scrolling
        if (!isUserScrolling && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Check if user is near the bottom of the chat
    const isNearBottom = () => {
        if (!chatContainerRef.current) return true;
        
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const threshold = 100; // pixels from bottom
        return scrollHeight - scrollTop - clientHeight < threshold;
    };

    // Handle scroll events to detect user scrolling
    const handleScroll = () => {
        if (chatContainerRef.current) {
            const nearBottom = isNearBottom();
            setIsUserScrolling(!nearBottom);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingMessage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!currentInput.trim() || isLoading || !connected) {
            return;
        }

        const userMessage = currentInput.trim();
        setCurrentInput('');
        setError(null);
        
        // Add user message to chat
        setMessages(prev => [...prev, {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        }]);

        setIsLoading(true);
        setStreamingMessage('');

        try {
            // Send message via socket.io
            socketRef.current.emit('analyze', {
                prompt: userMessage,
                session_id: sessionId
            });
        } catch (err) {
            console.error('Error:', err);
            setError(`Failed to send message: ${err.message}`);
            setIsLoading(false);
            sendError(err);
        }
    };

    const handleClear = () => {
        // Clear session history on backend
        if (socketRef.current && connected) {
            socketRef.current.emit('clear_session', {
                session_id: sessionId
            });
        }
        
        // Clear frontend state
        setMessages([]);
        setStreamingMessage('');
        setError(null);
        setCurrentInput('');
    };

    const handleStop = () => {
        if (socketRef.current) {
            socketRef.current.emit('stop');
            setIsLoading(false);
            setStreamingMessage('');
        }
    };

    return (
        <React.Fragment>
            <TopPanel label={demoModule} />
            <AppLayout
                navigation={<ServiceNavigation />}
                content={
                    <Content
                        messages={messages}
                        currentInput={currentInput}
                        setCurrentInput={setCurrentInput}
                        isLoading={isLoading}
                        streamingMessage={streamingMessage}
                        error={error}
                        connected={connected}
                        handleSubmit={handleSubmit}
                        handleClear={handleClear}
                        handleStop={handleStop}
                        messagesEndRef={messagesEndRef}
                        chatContainerRef={chatContainerRef}
                        handleScroll={handleScroll}
                    />
                }
                contentType="default"
                navigationOpen={props?.navigationState}
                onNavigationChange={e => props?.setNavigationState?.(e.detail.open)}
            />
        </React.Fragment>
    );
}

const Content = ({
    messages,
    currentInput,
    setCurrentInput,
    isLoading,
    streamingMessage,
    error,
    connected,
    handleSubmit,
    handleClear,
    handleStop,
    messagesEndRef,
    chatContainerRef,
    handleScroll
}) => {
    return (
        <React.Fragment>
            <BrowserCheck />
            <SpaceBetween direction='vertical' size='xs'>
                <SynopsisPanelV3 label="Synopsis" demotype={demoType} architectureImgs={[]}>
                    AI-powered tool that analyzes CloudFront service layer logs to diagnose performance, 
                    caching, TLS, and network issues.
                    <br/><br/>
                    <strong>How to Get Logs:</strong> Visit <Link external href="https://edge-tools.amazon.com/logs">Edge Tools</Link>, 
                    enter your RID (from <code>X-Amz-Cf-Id</code> header), and paste logs below.
                    <br/><br/>
                    <strong>What You Get:</strong> Request flow visualization, performance bottleneck analysis, 
                    TLS/SSL diagnostics, cache behavior insights, and actionable recommendations.
                    <br/><br/>
                    💡 Powered by Amazon Bedrock AgentCore with Claude Sonnet 4.5. 
                    Ask follow-up questions without re-pasting logs. Click "Clear Chat" to start fresh.
                </SynopsisPanelV3>

                {!connected && (
                    <Alert type="warning">
                        Connecting to analysis service...
                    </Alert>
                )}

                {error && (
                    <Alert type="error" dismissible onDismiss={() => {}}>
                        {error}
                    </Alert>
                )}

                <ChatContainer
                    messages={messages}
                    streamingMessage={streamingMessage}
                    isLoading={isLoading}
                    messagesEndRef={messagesEndRef}
                    chatContainerRef={chatContainerRef}
                    handleScroll={handleScroll}
                />

                <ChatInput
                    currentInput={currentInput}
                    setCurrentInput={setCurrentInput}
                    isLoading={isLoading}
                    connected={connected}
                    handleSubmit={handleSubmit}
                    handleClear={handleClear}
                    handleStop={handleStop}
                />

                <ExamplePrompts setCurrentInput={setCurrentInput} />
            </SpaceBetween>
        </React.Fragment>
    );
};

const ChatContainer = ({ messages, streamingMessage, isLoading, messagesEndRef, chatContainerRef, handleScroll }) => {
    return (
        <Container
            header={
                <Header variant="h2">
                    Analysis Chat
                </Header>
            }
        >
            <div 
                ref={chatContainerRef}
                onScroll={handleScroll}
                style={{ 
                    minHeight: '600px', 
                    maxHeight: '70vh', 
                    overflow: 'auto', 
                    padding: '16px' 
                }}
            >
                <SpaceBetween direction='vertical' size='m'>
                    {messages.length === 0 && !streamingMessage && (
                        <Box textAlign="center" color="text-body-secondary" padding="xxl">
                            <SpaceBetween direction="vertical" size="xs">
                                <Icon name="search" size="big" />
                                <Box variant="h3">Ready to analyze CloudFront logs</Box>
                                <Box>Paste your CloudFront service layer logs below to get started</Box>
                            </SpaceBetween>
                        </Box>
                    )}

                    {messages.map((message, index) => (
                        <ChatMessage key={index} message={message} />
                    ))}

                    {streamingMessage && (
                        <ChatMessage 
                            message={{ 
                                role: 'assistant', 
                                content: streamingMessage,
                                streaming: true 
                            }} 
                        />
                    )}

                    {isLoading && !streamingMessage && (
                        <Box>
                            <SpaceBetween direction="horizontal" size="xs">
                                <Avatar
                                    ariaLabel="CloudFront RID Analyzer"
                                    color="gen-ai"
                                    iconName="gen-ai"
                                    tooltipText="Analyzing..."
                                />
                                <LoadingBar variant="gen-ai" />
                            </SpaceBetween>
                        </Box>
                    )}

                    <div ref={messagesEndRef} />
                </SpaceBetween>
            </div>
        </Container>
    );
};

const ChatMessage = ({ message }) => {
    const isUser = message.role === 'user';
    const [copied, setCopied] = useState(false);
    
    const handleCopy = () => {
        navigator.clipboard.writeText(message.content).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    
    // Extract Redis cache indicator, flow visualization JSON, and remaining content
    let flowData = null;
    let contentWithoutFlow = message.content;
    let redisCacheIndicator = null;
    let preambleText = null;
    
    if (!isUser && !message.streaming) {
        // First, extract Redis cache indicator if present
        const redisCacheMatch = message.content.match(/^(⚡\s*\*\*\[Redis Cache\]\*\*[^\n]*\n+---\n+)/);
        if (redisCacheMatch) {
            redisCacheIndicator = redisCacheMatch[1].trim();
            contentWithoutFlow = message.content.substring(redisCacheMatch[0].length);
        }
        
        // Aggressively clean up excessive whitespace
        // Replace 3+ newlines with just 1 newline (not 2, to minimize space)
        contentWithoutFlow = contentWithoutFlow.replace(/\n{3,}/g, '\n');
        // Also clean up spaces before newlines
        contentWithoutFlow = contentWithoutFlow.replace(/ +\n/g, '\n');
        // Clean up multiple spaces
        contentWithoutFlow = contentWithoutFlow.replace(/  +/g, ' ');
        
        // Extract any text before the flow diagram
        const beforeFlowMatch = contentWithoutFlow.match(/^([\s\S]*?)(?=```flow|flow\s*\{)/);
        if (beforeFlowMatch && beforeFlowMatch[1].trim()) {
            preambleText = beforeFlowMatch[1].trim();
        }
        
        // Only try to parse flow data when message is complete (not streaming)
        // Try multiple patterns to extract flow data
        // Pattern 1: Proper markdown code fence with newlines
        let flowMatch = contentWithoutFlow.match(/```flow\n([\s\S]*?)\n```/);
        
        // Pattern 2: Code fence without newlines (```flow{...}```)
        if (!flowMatch) {
            flowMatch = contentWithoutFlow.match(/```flow\s*(\{[\s\S]*?\})\s*```/);
        }
        
        // Pattern 3: Just "flow" followed by JSON (flow{...})
        if (!flowMatch) {
            flowMatch = contentWithoutFlow.match(/flow\s*(\{[\s\S]*?\})\s*(?=###|##|\n\n|$)/);
        }
        
        if (flowMatch) {
            try {
                flowData = JSON.parse(flowMatch[1]);
                // Remove the preamble text and flow block from content
                if (preambleText) {
                    contentWithoutFlow = contentWithoutFlow.replace(preambleText, '');
                }
                // Remove the flow block from content - be more aggressive
                if (contentWithoutFlow.includes('```flow')) {
                    contentWithoutFlow = contentWithoutFlow.replace(/```flow[\s\S]*?```\s*/g, '');
                } else {
                    // Remove "flow{...}" pattern
                    contentWithoutFlow = contentWithoutFlow.replace(/flow\s*\{[\s\S]*?\}\s*/g, '');
                }
                // Clean up any leading/trailing whitespace
                contentWithoutFlow = contentWithoutFlow.trim();
            } catch (e) {
                console.error('Failed to parse flow data:', e);
                console.log('Flow match:', flowMatch[1]);
                // Don't try to extract flow data if parsing fails
                flowData = null;
            }
        }
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '12px'
        }}>
            {!isUser && (
                <Avatar
                    ariaLabel="CloudFront RID Analyzer"
                    color="gen-ai"
                    iconName="gen-ai"
                    tooltipText="CloudFront RID Analyzer"
                />
            )}
            {isUser && (
                <Avatar
                    ariaLabel="You"
                    iconName="user-profile"
                    tooltipText="You"
                />
            )}
            <Box 
                padding="m"
                style={{
                    flex: 1,
                    backgroundColor: isUser ? '#e8f4fd' : '#ffffff',
                    borderRadius: '8px',
                    border: isUser ? '2px solid #0972d3' : '1px solid #e1e4e8',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                {!isUser && (
                    <Button
                        variant="inline-icon"
                        iconName={copied ? "check" : "copy"}
                        onClick={handleCopy}
                        ariaLabel="Copy to clipboard"
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            zIndex: 10
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy'}
                    </Button>
                )}
                {isUser ? (
                    <pre style={{ 
                        margin: 0,
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word',
                        fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                        fontSize: '11px',
                        lineHeight: '1.6',
                        backgroundColor: '#f6f8fa',
                        padding: '12px',
                        borderRadius: '4px',
                        border: '1px solid #d0d7de',
                        overflowX: 'auto',
                        maxHeight: '400px',
                        overflowY: 'auto'
                    }}>
                        {message.content
                            .replace(/ EOE /g, '\nEOE\n\n')
                            .replace(/ Layer: /g, '\n\nLayer: ')
                            .replace(/ RID: /g, '\n\nRID: ')
                            .replace(/ Metadata Time:/g, '\n\nMetadata Time:')
                            .replace(/ -{10,} /g, '\n---------------------------------\n')
                            .replace(/ Loading service data/g, '\n\nLoading service data')
                            .replace(/ Program=/g, '\nProgram=')
                            .replace(/ LogGroupName:/g, '\nLogGroupName:')
                        }
                    </pre>
                ) : (
                    <div style={{ fontSize: '14px' }}>
                        {redisCacheIndicator && (
                            <div style={{ 
                                marginBottom: '16px',
                                padding: '12px 16px',
                                backgroundColor: '#f0f8ff',
                                border: '2px solid #0972d3',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span style={{ fontSize: '20px' }}>⚡</span>
                                <strong style={{ color: '#0972d3', fontSize: '14px' }}>
                                    [Redis Cache] This analysis was retrieved from Redis cache and has not been reprocessed.
                                </strong>
                            </div>
                        )}
                        {preambleText && (
                            <div style={{ 
                                marginBottom: '12px',
                                padding: '8px 12px',
                                backgroundColor: '#f6f8fa',
                                borderLeft: '3px solid #0972d3',
                                borderRadius: '4px',
                                fontSize: '14px',
                                color: '#24292f',
                                lineHeight: '1.6'
                            }}>
                                {preambleText}
                            </div>
                        )}
                        {flowData && <RequestFlowVisualization flowData={flowData} />}
                        <StructuredResponse content={contentWithoutFlow} isStreaming={message.streaming} />
                        {message.streaming && (
                            <div style={{ marginTop: '8px' }}>
                                <LoadingBar variant="gen-ai" />
                            </div>
                        )}
                    </div>
                )}
            </Box>
        </div>
    );
};

const ChatInput = ({ 
    currentInput, 
    setCurrentInput, 
    isLoading,
    connected,
    handleSubmit, 
    handleClear,
    handleStop 
}) => {
    return (
        <Container>
            <form onSubmit={handleSubmit}>
                <SpaceBetween direction="vertical" size="xs">
                    <Textarea
                        value={currentInput}
                        onChange={({ detail }) => setCurrentInput(detail.value)}
                        placeholder="Paste your CloudFront service layer logs here or ask a question..."
                        rows={6}
                        disabled={isLoading || !connected}
                    />
                    <ColumnLayout columns={2}>
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button 
                                variant="primary" 
                                onClick={handleSubmit}
                                disabled={isLoading || !currentInput.trim() || !connected}
                            >
                                {isLoading ? 'Analyzing...' : 'Analyze'}
                            </Button>
                            {isLoading && (
                                <Button 
                                    variant="normal"
                                    onClick={handleStop}
                                >
                                    Stop
                                </Button>
                            )}
                            <Button 
                                variant="normal" 
                                onClick={handleClear}
                                disabled={isLoading}
                            >
                                Clear Chat
                            </Button>
                        </SpaceBetween>
                        <Box textAlign="right" color="text-body-secondary">
                            {isLoading && <LoadingBar variant="gen-ai" />}
                            {!connected && <Box color="text-status-warning">Connecting...</Box>}
                        </Box>
                    </ColumnLayout>
                </SpaceBetween>
            </form>
        </Container>
    );
};

const ExamplePrompts = ({ setCurrentInput }) => {
    const examples = [
        "What can you help me analyze?",
        "Why is this request slow?",
        "Explain the cache behavior",
        "How can I improve performance?",
    ];

    return (
        <Container
            header={
                <Header variant="h3">
                    Example Follow-up Prompts
                </Header>
            }
        >
            <Box variant="small" color="text-body-secondary" padding={{ bottom: "xs" }}>
                💡 Ask follow-up questions without re-pasting logs. Clear chat to start fresh.
            </Box>
            <SpaceBetween direction="vertical" size="xs">
                <SpaceBetween direction="horizontal" size="xs">
                    {examples.map((example, index) => (
                        <Button
                            key={index}
                            variant="inline-link"
                            onClick={() => setCurrentInput(example)}
                        >
                            {example}
                        </Button>
                    ))}
                </SpaceBetween>
                <Button
                    variant="primary"
                    iconName="upload"
                    onClick={() => setCurrentInput(EXAMPLE_LOG)}
                >
                    Load Example Log
                </Button>
            </SpaceBetween>
        </Container>
    );
};
